/**
 * Abstração única para leitura/gravação do token JWT.
 *
 * Hoje (v2): usa localStorage. Mantém leitura de cookie como fallback
 * de compatibilidade durante a migração (caso o usuário tenha sessão
 * ativa do esquilo antes do deploy do coruja standalone).
 *
 * Quando o esquilo morrer (ver `cleanup/02-remove-esquilo-cli`),
 * remover o fallback de cookie e o `js-cookie` das deps.
 *
 * @module core/session/token-storage
 */
import cookies from 'js-cookie';

const STORAGE_KEY = 'token';

/**
 * Lê o token JWT.
 *
 * Ordem de busca:
 *   1. localStorage (formato canônico v2)
 *   2. cookie 'token' (legado do esquilo) — migra pra localStorage
 *      automaticamente se encontrado
 *   3. input hidden 'zgTrexEsquiloID' (modo headless antigo, deprecated)
 *
 * @returns {string|null} JWT ou null se não houver sessão.
 */
export function getToken() {
  const fromStorage = localStorage.getItem(STORAGE_KEY);
  if (fromStorage) return fromStorage;

  const fromCookie = cookies.get(STORAGE_KEY);
  if (fromCookie) {
    localStorage.setItem(STORAGE_KEY, fromCookie);
    return fromCookie;
  }

  const tokenNode = document.getElementById('zgTrexEsquiloID');
  if (tokenNode && tokenNode.value) {
    return tokenNode.value;
  }

  return null;
}

/**
 * Grava o token JWT em localStorage e também em cookie por enquanto
 * (compat com código antigo que ainda lê de cookie diretamente).
 *
 * @param {string} token JWT a persistir.
 */
export function setToken(token) {
  if (!token) return;
  localStorage.setItem(STORAGE_KEY, token);
  cookies.set(STORAGE_KEY, token);
}

/**
 * Limpa o token de todos os storages. Usado em logout e em 401.
 */
export function clearToken() {
  localStorage.removeItem(STORAGE_KEY);
  cookies.remove(STORAGE_KEY);
}
